import React from 'react';
import { LuSettings } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MultiCombobox } from '@/components/ui/multi-combobox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useHouseholdRoutePaths } from 'utils';

import { ReportingAccountSelector } from './ReportingAccountSelector';
import { ReportingDateRangePicker } from './ReportingDateRangePicker';
import { ReportingExportButton } from './ReportingExportButton';
import { useReportingAccounts } from './useReportingAccounts';
import { useReportingBenchmarks } from './useReportingBenchmarks';
import { useReportingDateRange } from './useReportingDateRange';
import { useReportingIncludeClosed } from './useReportingIncludeClosed';

interface ReportingHeaderProps {
    householdGUID: string;
    // Opt-out for point-in-time tabs (Holdings): the range picker would render but scope nothing.
    // The URL still carries the range across tabs, so hiding the control loses no state.
    showDateRange?: boolean;
}

// "Compared against" as the placeholder; once picked, the first benchmark (the count lives in the
// corner badge, as on the account selector). MultiCombobox passes the values in the order they were
// picked; its labels follow the option order instead.
const benchmarkSummary = (_labels: string[], names: string[]) =>
    names.length > 0 ? `Compared against: ${names[0]}` : 'Compared against';

/**
 * The reporting tabs' filter bar: account, date-range and benchmark selectors on the left; the export
 * and the settings link on the right. The closed-account toggle lives inside the account selector.
 *
 * All filters come from the URL-backed hooks rather than props, so a tab reads the same values
 * for its own queries without anything being threaded back through the header.
 */
export const ReportingHeader = ({ householdGUID, showDateRange = true }: ReportingHeaderProps) => {
    const { selectedAccountGUIDs } = useReportingAccounts();
    const { dateRange, setDateRange } = useReportingDateRange();
    const { includeClosed } = useReportingIncludeClosed();
    const { benchmarkNames, availableBenchmarks, setBenchmarkNames } = useReportingBenchmarks();
    const { HouseholdIndividualPageRoutePath } = useHouseholdRoutePaths();

    return (
        <div className="flex flex-wrap items-center gap-3">
            <ReportingAccountSelector householdGUID={householdGUID} />
            {showDateRange ? <ReportingDateRangePicker value={dateRange} onChange={setDateRange} /> : null}
            {/* Moved here from Advanced Analytics, so the chart and the statistics share the benchmarks.
                Hidden when there is no benchmark reference data to compare against. The trigger is the
                same outline button as the account selector. */}
            {availableBenchmarks.length > 0 ? (
                // Corner count badge copied from ReportingAccountSelector's trigger.
                <span className="relative inline-flex">
                    <MultiCombobox
                        options={availableBenchmarks.map((name) => ({ value: name, label: name }))}
                        value={benchmarkNames}
                        onValueChange={setBenchmarkNames}
                        // Only the search box shows it ("Search benchmarks..."); the trigger uses benchmarkSummary.
                        placeholder="Benchmarks"
                        renderSummary={benchmarkSummary}
                        className="w-72 font-normal"
                    />
                    {benchmarkNames.length > 0 ? (
                        <Badge className="pointer-events-none absolute -top-1.5 -right-1.5 h-5 min-w-5 justify-center px-1">
                            {benchmarkNames.length}
                        </Badge>
                    ) : null}
                </span>
            ) : null}
            {/* ml-auto pushes the actions to the right edge; the filters stay on the left. items-start
                because the export can show an error line under itself. */}
            <div className="ml-auto flex items-start gap-2">
                <ReportingExportButton
                    globalAccountGUID={householdGUID}
                    range={dateRange}
                    selectedAccountGUIDs={selectedAccountGUIDs}
                    includeClosed={includeClosed}
                />
                <Tooltip>
                    <TooltipTrigger asChild>
                        {/* Same height and radius as the export button beside it. */}
                        <Button asChild variant="outline" size="icon" className="rounded-lg">
                            <Link
                                to={HouseholdIndividualPageRoutePath(householdGUID, 'reporting/settings')}
                                aria-label="Reporting Settings"
                            >
                                <LuSettings />
                            </Link>
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>Reporting Settings</TooltipContent>
                </Tooltip>
            </div>
        </div>
    );
};
